/* Product route only. The sticky bar relays the existing form, never creates a cart request. */
if (!customElements.get('one-sticky-purchase')) {
  customElements.define('one-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.host = this.closest('product-info');
      if (!this.host || this.host.dataset.updateUrl === 'false' || this.closest('quick-add-modal')) return;
      this.purchase = this.host.querySelector('product-form');
      this.info = this.host.querySelector('.product__info-container');
      this.button = this.querySelector('[data-one-sticky-submit]');
      this.priceOutput = this.querySelector('[data-one-sticky-price]');
      this.errorOutput = this.querySelector('[data-one-sticky-error]');
      if (!this.purchase || !this.info || !this.button || !('IntersectionObserver' in window)) return;
      this.mobile = matchMedia('(max-width: 749px)');
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.hidden) document.documentElement.style.setProperty('--one-sticky-height', `${this.getBoundingClientRect().height}px`);
        });
        this.resizeObserver.observe(this);
      }
      const refresh = () => { this.syncState(); this.syncVisibility(); };
      this.observer = new IntersectionObserver(() => this.syncVisibility());
      this.observer.observe(this.purchase);
      // A chapter link can jump from below to above the viewport without intersecting the form.
      window.addEventListener('scroll', () => {
        if (!this.mobile.matches || this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.syncVisibility(); });
      }, { passive: true, signal: this.abort.signal });
      document.addEventListener('focusin', () => this.syncVisibility(), { signal: this.abort.signal });
      document.addEventListener('focusout', () => requestAnimationFrame(() => { if (this.isConnected) this.syncVisibility(); }), { signal: this.abort.signal });

      this.contentObserver = new MutationObserver(refresh);
      this.contentObserver.observe(this.info, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'hidden'] });
      // Native Taste dialogs plus explicitly opened third-party aria-modal dialogs.
      this.overlayObserver = new MutationObserver(records => {
        if (records.some(record => record.target.matches('body, dialog, modal-dialog, product-modal, quick-add-modal, cart-drawer, #cart-notification, header-drawer details, details-modal details, [role="dialog"]'))) this.syncVisibility();
      });
      this.overlayObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class', 'open', 'hidden', 'aria-hidden'] });
      this.mobile.addEventListener('change', refresh, { signal: this.abort.signal });
      this.info.addEventListener('change', refresh, { signal: this.abort.signal });
      this.button.addEventListener('click', () => this.submit(), { signal: this.abort.signal });
      this.querySelector('a').addEventListener('click', event => {
        event.preventDefault(); this.showPurchase();
      }, { signal: this.abort.signal });
      refresh();
    }
    hasOverlay() {
      return Boolean(document.querySelector('dialog[open], modal-dialog[open], product-modal[open], quick-add-modal[open], cart-drawer.active, #cart-notification.active, header-drawer details[open], details-modal details[open], [role="dialog"][aria-modal="true"][aria-hidden="false"]'));
    }
    syncVisibility() {
      const typing = document.activeElement?.matches?.('textarea, input:not([type="button"]):not([type="submit"]):not([type="radio"]):not([type="checkbox"]), select');
      this.hidden = !(this.mobile.matches && this.purchase.getBoundingClientRect().bottom < 0 && !this.hasOverlay() && !typing);
    }
    syncState() {
      const original = this.purchase.querySelector('[type="submit"]');
      if (!original) { this.button.disabled = true; return; }
      const busy = original.getAttribute('aria-disabled') === 'true' || original.classList.contains('loading');
      this.button.disabled = original.disabled || busy;
      this.button.setAttribute('aria-busy', String(busy));
      this.button.textContent = original.disabled || busy
        ? original.querySelector('span').textContent.trim()
        : this.dataset.addLabel || window.variantStrings.addToCart;
      const source = this.host.querySelector(`[id="price-${this.host.dataset.section}"]`);
      const price = source?.querySelector('.price');
      const priceKey = price && !source.classList.contains('hidden') ? price.outerHTML : '';
      if (priceKey !== this.lastPrice) {
        this.lastPrice = priceKey;
        const clone = priceKey ? price.cloneNode(true) : null;
        clone?.removeAttribute('id');
        clone?.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        this.priceOutput.replaceChildren(...(clone ? [clone] : []));
      }
      const error = this.purchase.querySelector('.product-form__error-message-wrapper');
      const message = error && !error.hidden ? error.querySelector('.product-form__error-message')?.textContent || '' : '';
      this.errorOutput.textContent = message;
      this.errorOutput.hidden = !message;
    }
    showPurchase() {
      this.info.setAttribute('tabindex', '-1');
      this.info.scrollIntoView({ block: 'start', behavior: 'instant' });
      this.info.focus({ preventScroll: true });
      this.hidden = true;
    }
    submit() {
      this.syncState();
      if (this.button.disabled || this.hasOverlay()) return;
      const form = this.purchase.querySelector('form');
      const original = this.purchase.querySelector('[type="submit"]');
      if (!form || !original) return;
      if (!form.checkValidity()) { this.showPurchase(); form.reportValidity(); return; }
      // Taste stores the focused control for cart focus restoration. Use its original button.
      original.focus({ preventScroll: true });
      form.requestSubmit(original);
      this.syncState();
    }
    disconnectedCallback() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.observer?.disconnect(); this.contentObserver?.disconnect(); this.overlayObserver?.disconnect(); this.abort?.abort();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        document.documentElement.style.removeProperty('--one-sticky-height');
      }
    }
  });
}
if (!customElements.get('one-note-journey')) {
  customElements.define('one-note-journey', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.stages = [...this.querySelectorAll('[data-note-stage]')];
      const observe = () => {
        this.observer?.disconnect();
        this.stages.forEach(stage => stage.classList.remove('is-current'));
        if (this.motion.matches || !('IntersectionObserver' in window)) return;
        const visibility = new Map();
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => visibility.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));
          const current = [...visibility].filter(([,ratio]) => ratio > 0).sort((a,b) => b[1] - a[1])[0]?.[0];
          this.stages.forEach(stage => stage.classList.toggle('is-current', stage === current));
        }, { rootMargin: '-15% 0px -20% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] });
        this.stages.forEach(stage => this.observer.observe(stage));
      };
      this.motion.addEventListener('change', observe, { signal: this.abort.signal }); observe();
    }
    disconnectedCallback() { this.observer?.disconnect(); this.abort?.abort(); }
  });
}
